<template>
  <u-button v-if="!disabled" :size="size" @click="handleClick" data-testid="components-verifycodebtn-click-btn">
    {{ $t('components.VerifyCodeBtn.9b8d9b4eb58fc86a48df9778ea42bc28') }}
  </u-button>

  <u-button v-else disabled :size="size" data-testid="components-verifycodebtn-u-button-btn">
    {{ timeCount
    }}{{ $t('components.VerifyCodeBtn.a063c3b2c3dfb34340a8c1e0ea80d6d3') }}
  </u-button>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, onBeforeUnmount, watch } from 'vue';
import { emailReg, phoneReg } from '@/libs/reg';
import { message } from '@koi-design/uxd-ui';
import { getEmialCodeAPi, getPhoneCodeAPi } from '@api/permission';

export default defineComponent({
  props: {
    mode: {
      type: String,
      validator(value) {
        return ['phone', 'email', 'unknown'].includes(value);
      },
      default: 'email'
    },
    phone: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    },
    verifyType: {
      type: String,
      default: 'user_register'
    },
    captchaUid: {
      type: String,
      default: ''
    },
    captcha: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'large'
    }
  },
  emits: ['get-code', 'refresh-captcha'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const timeCount = ref(60);
    const disabled = ref(false);
    const loading = ref(false);
    let timer = null;
    watch(
      () => props.mode,
      () => {
        timer && clearInterval(timer);
        disabled.value = false;
        timeCount.value = 60;
      }
    );
    const noCapchaTypes = ['bind_mobile', 'bind_mail', 'sms_login'];
    const handleClick = async () => {
      if (props.mode === 'unknown') {
        message.error(
          t('components.VerifyCodeBtn.a0affdc79c86c70fc245fd33c8ef7139')
        );
        return;
      }
      if (!noCapchaTypes.includes(props.verifyType) && !props.captcha.trim()) {
        message.error(t('message.d11ad6f111dea929d0c7b3cff0cd34fd'));
        return;
      }
      if (disabled.value || loading.value) {
        return;
      }
      const afterSend = res => {
        if (res.data.code === '000000') {
          message.success(res.data.message);
          disabled.value = true;
          timer && clearInterval(timer);
          timer = setInterval(() => {
            if (timeCount.value <= 0) {
              clearInterval(timer);
              disabled.value = false;
              timeCount.value = 60;
            }
            timeCount.value -= 1;
          }, 1000);
        } else {
          message.error(res.data.message);
          emit('refresh-captcha');
        }
        loading.value = false;
      };
      if (props.mode === 'phone') {
        if (!props.phone.trim()) {
          message.error(t('message.d9933c1ab3e2c276e3e64cb6790092b9'));
          return;
        }
        if (!phoneReg.test(props.phone)) {
          message.error(
            t('components.VerifyCodeBtn.ac3fe076ae4126ce1a0b5739cd8c5bf7')
          );
          return;
        }
        loading.value = true;
        const res = await getPhoneCodeAPi({
          mobile: props.phone,
          verify_type: props.verifyType,
          captcha_uid: props.captchaUid,
          captcha: props.captcha
        });
        afterSend(res);
        return;
      }
      if (!emailReg.test(props.email)) {
        message.error(
          t('components.VerifyCodeBtn.8e8daa39a38487e769b813de37352095')
        );
        return;
      }
      loading.value = true;
      const res = await getEmialCodeAPi({
        email: props.email,
        verify_type: props.verifyType,
        captcha_uid: props.captchaUid,
        captcha: props.captcha
      });
      afterSend(res);
    };
    onBeforeUnmount(() => {
      timer && clearInterval(timer);
    });
    return {
      timeCount,
      disabled,
      handleClick
    };
  }
});
</script>

<style scoped lang="less">
.btn {
  font-size: 14px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.disabled {
    border: 1px solid var(--border-neutral-color-secondary);
  }
}
</style>
